import { List, Set } from "immutable";
import { flatten, isNil, map, uniq } from "lodash";
// import { Stopwatch } from "performance-stopwatch";
import createCachedSelector from "re-reselect";
import { createSelector } from "reselect";
import { createMapSelector } from "reselect-map";

import { MAX_INITIALS_LENGTH, MAX_SHORT_LABEL_LENGTH, WORDS_OMITTED_FROM_INITIALS } from "../../constants/itemTexts";
import { createFromJS, immutableRecords, Precision } from "../../types";
import { listToIndexMap } from "../../utils/immutableExtensions";
import { getLabelWithUntitled } from "../../utils/labelUtils";
import attachmentSelectors from "../attachments/selectors";
import dateFunctions, { isDateValid } from "../definition/calendar/dateFunctions";
import dateByAddingSpan from "../definition/calendar/dateFunctions/dateByAddingSpan";
import dateSubtractionFunctions from "../definition/calendar/dateFunctions/dateSubtractionFunctions";
import { timespanForPrecisionAndUnits } from "../definition/calendar/dateFunctions/timespanFunctions";
import calendarSelectors from "../definition/calendar/selectors";
import { narrativeSettingsSelectors } from "../definition/narrativeSettings";
import definitionSelectors from "../definition/selectors";
import typeSelectors from "../definition/types/selectors";
import narrativeSelectors from "../narrative/selectors";
import { findItemPosition as findItemNarrativePosition } from "../narrative/utils";
import sequenceSelectors from "../sequence/selectors";
import superAndChildrenSelectors from "../superAndChildrenById/selectors";

const getItemSwatchColor = (colorMap, obj) => {
  const namedColor = colorMap.get(obj.color);
  return namedColor ? namedColor.swatchColor : obj.color;
};

const getItemIsEvent = (state, itemId) =>
  typeSelectors.getTypeIsAllowedInTimeline(state, state.getIn(["data", "items", "byId", itemId, "type"]));

const getItemIsLocked = (state, itemId) => state.getIn(["data", "items", "byId", itemId, "isDateLocked"]);

const getItemsAreEvents = (state, itemIds) => List(itemIds).every(id => getItemIsEvent(state, id));
const getItemsAreUnlocked = (state, itemIds) => List(itemIds).every(id => !getItemIsLocked(state, id));

const getItemIsAllowedInNarrative = (state, objId) => {
  const obj = state.getIn(["data", "items", "byId", objId]);
  return obj ? typeSelectors.getTypeIsAllowedInNarrative(state, obj.type) : false;
};

const getItemsAreAllowedInNarrative = (state, itemIds) =>
  List(itemIds).every(id => getItemIsAllowedInNarrative(state, id));

const getItemIsAllowedInSequence = (state, objId) => {
  const obj = state.getIn(["data", "items", "byId", objId]);
  if (!obj) {
    return false;
  }
  return typeSelectors.getTypeIsAllowedInSequence(state, obj.type);
};

const getItemsAreAllowedInSequence = (state, itemIds) =>
  List(itemIds).every(id => getItemIsAllowedInSequence(state, id));

const getItemIsAllowedToDisplayInTimeline = (state, objId) => {
  const obj = state.getIn(["data", "items", "byId", objId]);
  if (!obj) {
    return false;
  }
  return typeSelectors.getTypeIsDisplayedInTimeline(state, obj.type);
};

const getItemsAreAllowedToDisplayInTimeline = (state, itemIds) =>
  List(itemIds).every(id => getItemIsAllowedToDisplayInTimeline(state, id));

const getItemWithTypeAllowedToDisplayInTimeline = (state, objId) => {
  const obj = state.getIn(["data", "items", "byId", objId]);
  if (!obj) {
    return false;
  }
  return typeSelectors.getTypeIsAllowedInTimeline(state, obj.type);
};

const getItemsWithTypeAllowedToDisplayInTimeline = (state, itemIds) =>
  List(itemIds).every(id => getItemWithTypeAllowedToDisplayInTimeline(state, id));

const getAllItemIdsAllowedInNarrative = state => {
  const narrativeTypeIds = typeSelectors.getNarrativeTypeIds(state);
  return getItemIdsOfTypes(state, narrativeTypeIds);
};

const getEntityForGuid = (state, entityId) => state.getIn(["data", "items", "byId", entityId]);

const getItemsOfType = (state, typeId) => {
  return getItemIdsOfType(state, typeId).map(oId => state.data.items.byId.get(oId));
};

const getItemsOfTypes = (state, types) => {
  return getItemIdsOfTypes(state, types).map(oId => state.data.items.byId.get(oId));
};

const getItemIdsOfType = (state, typeId) => {
  return state.data.items.allIdsForType.get(typeId);
};

const getItemIdsOfTypes = createCachedSelector(
  // inputs
  (state, _typeIds) => getItemIdsByType(state),
  (state, typeIds) => typeIds,
  // output
  (itemsByType, typeIds) => {
    return itemsByType
      .filter((list, typeId) => {
        return typeIds.includes(typeId);
      })
      .toList()
      .flatten(true);
  }
)((state, types) => types.join(","));

const getItemIdsOfTypesWithParentTypes = createSelector(
  (state, typeIds) => typeSelectors.getAllSuperTypesOfTypes(state, typeIds),
  state => state,
  (_state, typeIds) => typeIds,
  (superTypeIds, state, typeIds) => {
    return getItemIdsOfTypes(state, superTypeIds.concat(typeIds));
  }
);

const getItemIdsByType = createSelector(
  typeSelectors.getOrderedTypeIds,
  state => state.data.items.allIdsForType,
  (typeIds, allIdsByType) => {
    let result = allIdsByType;
    result = result.withMutations(m => {
      typeIds.forEach(id => {
        if (!allIdsByType.get(id)) {
          m.set(id, List());
        }
      });
    });
    return result;
  }
);

const getItemIdSetByType = createSelector(
  // input
  getItemIdsByType,
  // output
  idsByType => idsByType.map(ids => ids.toSet())
);

const getItemIndexByTypeFromMap = createSelector(
  // input
  idsByType => idsByType,
  // output
  idsByType => idsByType.map(listToIndexMap)
);

/**
 * @param {TimelineRecord} state
 */
const getItemIndexByType = state => getItemIndexByTypeFromMap(getItemIdsByType(state));

const getSidebarTypesHasItem = state => {
  const sidebarTypes = typeSelectors.getSidebarTypes(state);
  return sidebarTypes.filter(t => getItemIdsOfType(state, t.id).size > 0);
};

/**
 * Recently it filters out the narrative only type
 * when the item doesn't have a narrative position
 */
const getAllowedTypeOptionsByItems = (state, itemIds) => {
  const hasNoNarrativePositionItem =
    itemIds.findIndex(id => narrativeSelectors.getNarrativePositionForItemId(state, id).size === 0) >= 0;
  let types = hasNoNarrativePositionItem
    ? typeSelectors.getOrderedNoNarrativeOnlyTypes(state)
    : typeSelectors.getOrderedTypes(state);

  if (narrativeSettingsSelectors.getNarrativeUsesStrictFolderStructure(state)) {
    const itemsById = getItemsById(state);
    const itemTypeIds = Set(itemIds.map(id => itemsById.get(id).type)).toList();
    const itemTypes = itemTypeIds.map(tId => typeSelectors.getTypeForId(state, tId));
    const anItemIsFolder = Boolean(itemTypes.find(t => t.isNarrativeFolder));
    const anItemIsNotFolder = Boolean(itemTypes.find(t => !t.isNarrativeFolder));
    if (anItemIsFolder && anItemIsNotFolder) {
      types = List();
    } else if (anItemIsFolder) {
      types = types.filter(t => t.isNarrativeFolder);
    } else if (anItemIsNotFolder) {
      types = types.filter(t => !t.isNarrativeFolder);
    }
  }

  return types.map(typeSelectors.getTypeOptionFromType);
};

const getItemsById = state => state.data.items.byId;
const getItemIds = state => state.data.items.allIds;

const getItemsAsList = state => state.data.items.allIds.map(id => getItemsById(state).get(id));

const getColumnItemIdsByType = createSelector(
  getItemIdsByType,
  definitionSelectors.getTypesById,
  (objIdsByType, typesById) => objIdsByType.filter((_idList, type) => typesById.getIn([type, "showAsColumn"]))
);

const getColumnItemIds = createSelector(getColumnItemIdsByType, objIdsByType => {
  let idList = List([]);
  objIdsByType.forEach(ids => {
    idList = idList.concat(ids);
  });
  return idList;
});

const getItemFromId = (state, id) => getItemsById(state).get(id);

const getItemTypesForIds = (state, ids) => Set(ids.map(id => getItemFromId(state, id).type));

const getItemsWithIdsFromItems = (items, ids) => {
  return ids.map(id => items.byId.get(id));
  // return items.allIds.map(oId => items.byId.get(oId)).filter(e => ids.contains(e.id));
};

const getEntityLabelForGuid = (state, entityId) => getLabelWithUntitled(getEntityForGuid(state, entityId).get("label"));

const getEntityValuesForGuid = (state, entityId) => {
  const entity = getEntityForGuid(state, entityId);
  const typeId = entity.get("type");
  const typeData = typeSelectors.getItemTypeValuesForGuid(state, typeId);
  return immutableRecords.EntityDisplayValues({
    id: entityId,
    label: entity.get("label"),
    color: entity.get("color"),
    initials: entity.get("initials"),
    type: typeData
  });
};

const getReferenceIdsThatReferenceEntity = (state, entityId) =>
  getEntityForGuid(state, entityId).get("inverseReferences");

const getItemIdsThatReferenceEntity = (state, entityId) =>
  getReferenceIdsThatReferenceEntity(state, entityId).map(rId =>
    state.getIn(["data", "relationships", "byId", rId, "subject"])
  );

const getItemIdsThatReferenceEntity_RESELECT_SAFE = (entitiesById, relationshipsById, entityId) =>
  entitiesById.getIn([entityId, "inverseReferences"]).map(rId => relationshipsById.getIn([rId, "subject"]));

const getEarliestEventThatReferencesEntity = (state, entityId) => {
  const referencingItemIds = getItemIdsThatReferenceEntity(state, entityId);
  return getEarliestEventFromIds(state, referencingItemIds);
};

const getAllEventIds = createSelector(
  // inputs
  state => state.data.items.allIds,
  state => state.data.items.byId,
  typeSelectors.getTypeSetAllowedInTimeline,
  // output
  (allIds, itemsById, typeSet) => {
    return allIds.filter(id => typeSet.has(itemsById.get(id).type));
  }
);

const getAllEventIdsDisplayedOnTimeline = createSelector(
  // inputs
  state => state.data.items.allIds,
  state => state.data.items.byId,
  typeSelectors.getTypeSetDisplayedInTimeline,
  // output
  (allIds, itemsById, typeSet) => {
    return allIds.filter(id => typeSet.has(itemsById.get(id).type));
  }
);

// const getRawEvents = state => {
//   const result = state.getIn(["data", "items", "byId"]).filter((obj, id) => getItemIsEvent(state, id));
//   return result.toList();
// };

const labelToInitials = (label, omit) => {
  if (isNil(label) || label.length < 1) {
    return "N/A";
  }
  const omittedWords = omit.reduce((result, word, idx) => (idx > 0 ? `${word}\\b|${result}` : `${word}\\b`), "");
  const omittedMatch = new RegExp(`(?!(${omittedWords}))\\b[A-za-z]`, "ig");
  const matchAll = new RegExp(`\\b[A-za-z]`, "ig");
  const omittedInitials = label.match(omittedMatch);
  const words = omittedInitials ? omittedInitials : label.match(matchAll);
  return words ? words.join("").toUpperCase() : "";
};

const getInitialsForItem = item =>
  (isNil(item.initials) || item.initials === ""
    ? labelToInitials(item.label, WORDS_OMITTED_FROM_INITIALS)
    : item.initials
  ).slice(0, MAX_INITIALS_LENGTH);

const getShortLabelForItem = item =>
  (!item.shortLabel ? item.label || "" : item.shortLabel).slice(0, MAX_SHORT_LABEL_LENGTH);

const getCompactDisplayForItem = (item, type) => {
  const isAvatarType = typeSelectors.isAvatarType(type);
  const isShortLabelType = typeSelectors.isShortLabelType(type);
  return isAvatarType ? getInitialsForItem(item) : isShortLabelType ? getShortLabelForItem(item) : item.displayId;
};

const getItemsWithParent = createSelector(
  // Input function
  getItemsById,
  // Output
  itemsById => itemsById.filter(item => item.super)
);

const getItemsGroupedBySuper = state => getItemsById(state).groupBy(o => o.super);

const getItemsWithSetDates = createSelector(getItemsById, items => items.filter(item => isDateValid(item.startDate)));

const getNarrativePositionsForItems = createMapSelector(
  (_state, itemIds) => itemIds,
  state => state.data.narrative,
  (id, narrativeTree) => {
    const position = findItemNarrativePosition(narrativeTree, id);
    return position;
  }
);

const getItemsAreInNarrative = createSelector(
  (_state, itemIds) => itemIds,
  state => state.data.narrative,
  (itemIds, narrativeTree) => List(itemIds).every(id => findItemNarrativePosition(narrativeTree, id).length > 0)
);

const getImageIdsForItems = (state, ids) => {
  return uniq(map(ids, id => getItemFromId(state, id).image));
};

const getAttachmentIdsForItems = (state, ids) => {
  return uniq(flatten(map(ids, id => getItemFromId(state, id).attachments)));
};

const getTopLevelItems = createSelector(
  // inputs
  getItemsById,
  typeSelectors.getTypesById,
  superAndChildrenSelectors.getAncestorsMapFromState,
  // output
  (itemsById, typesById, ancestorsMapFromState) =>
    itemsById
      .filter(obj => {
        // If it has no super, it's a top level item
        if (!obj.super) {
          return true;
        }
        // If none of its ancestors can have dates, count it as a top level item
        const superIds = ancestorsMapFromState.get(obj.id);
        if (superIds) {
          const superAllowsDates = superIds.find(id => typesById.get(itemsById.get(id).type).canHaveDate);
          if (!superAllowsDates) {
            return true;
          }
        }
        return false;
      })
      .map(obj => obj.id)
      .toList()
);

const getEventStartTs = ev => {
  return ev.startDate.timestamp;
};

const getEventEndTs = (calendar, ev) => {
  return isDateValid(ev.startDate) ? dateByAddingSpan(calendar, ev.startDate, ev.duration).timestamp : null;
};

const getEventStartDate = ev => {
  return ev.startDate;
};

const getEventEndDate = (calendar, ev) => {
  return isDateValid(ev.startDate) ? dateByAddingSpan(calendar, ev.startDate, ev.duration) : null;
};

const getEarliestEventFromIds = (state, eventIds) => {
  return getEarliestEventFromIdsInner(getItemsById(state), eventIds);
};

const getEndEventFromIdsInner = (itemsById, eventIds, isStartEnd = true) => {
  let resultEvent = null;
  let targetTs = null;
  eventIds.forEach(id => {
    const e = itemsById.get(id);
    const ts = getEventStartTs(e);
    if (!isNil(ts) && (resultEvent === null || (isStartEnd ? ts < targetTs : ts > targetTs))) {
      resultEvent = e;
      targetTs = ts;
    }
  });
  return resultEvent;
};

const getEarliestEventFromIdsInner = (itemsById, eventIds) => {
  return getEndEventFromIdsInner(itemsById, eventIds, true);
};

const getLatestEventFromIdsInner = (itemsById, eventIds) => {
  return getEndEventFromIdsInner(itemsById, eventIds, false);
};

const getEarliestItemOfAll = createSelector(getItemsById, getAllEventIds, getEarliestEventFromIdsInner);
const getLatestItemOfAll = createSelector(getItemsById, getAllEventIds, getLatestEventFromIdsInner);

const getEarliestTimelineEvent = createSelector(
  // inputs
  getItemsById,
  getAllEventIdsDisplayedOnTimeline,
  // output
  getEarliestEventFromIdsInner
);

const getLatestTimelineEvent = createSelector(
  // inputs
  getItemsById,
  getAllEventIdsDisplayedOnTimeline,
  // output
  getLatestEventFromIdsInner
);

const getDateRangeForTimelineContent = createSelector(
  // inputs
  getAllEventIdsDisplayedOnTimeline,
  getItemsById,
  calendarSelectors.getCalendar,
  // output
  (timelineIds, itemsById, calendar) => {
    let earliest = null;
    let latest = null;
    timelineIds.forEach(id => {
      const item = itemsById.get(id);
      const s = getEventStartDate(item);
      const e = getEventEndDate(calendar, item);
      if (isDateValid(s)) {
        earliest = !isDateValid(earliest) || s.timestamp < earliest.timestamp ? s : earliest;
      }
      if (isDateValid(e)) {
        latest = !isDateValid(latest) || e.timestamp > latest.timestamp ? e : latest;
      }
    });
    return immutableRecords.TimelineRangeRecord({
      from: earliest,
      to: latest
    });
  }
);

const getTimestampRange = createSelector(
  // inputs
  getItemsById,
  calendarSelectors.getCalendar,
  // output
  (itemsById, calendar) => {
    let earliestTs = null;
    let latestTs = null;
    itemsById.forEach(item => {
      const s = getEventStartTs(item);
      const e = getEventEndTs(calendar, item);
      if (!isNil(s)) {
        earliestTs = isNil(earliestTs) ? s : Math.min(earliestTs, s);
      }
      if (!isNil(e)) {
        latestTs = isNil(latestTs) ? e : Math.max(latestTs, e);
      }
    });
    return immutableRecords.RangeRecord({
      min: earliestTs,
      max: latestTs,
      range: isNil(latestTs) || isNil(earliestTs) ? null : latestTs - earliestTs
    });
  }
);

// May choose to switch this to list selector if it is slow enough, but suspect it is a fast function so avoiding
// premature optimisation.
const getItemsWithAnniversaries = createSelector(
  // inputs
  state => state.data.items.byId,
  // calculate based on inputs
  items => items.filter(obj => obj.showAnniversary).toList()
);

const getItemsWithAnniversariesMap = createSelector(
  // inputs
  state => state.data.items.byId,
  // calculate based on inputs
  items => items.filter(obj => obj.showAnniversary)
);

// Merges the event layout with the current selection.
// Receives: state, viewId
const getDisplayAnniversariesBetweenDates = (
  items,
  typesById,
  colorMap,
  calendar,
  startTs,
  endTs,
  minRepeatSeconds,
  dateForNow
) => {
  let repeatFrequency = timespanForPrecisionAndUnits(Precision.Year, 1);
  let repeatEveryXFrequency = 1;
  const avgRepeatSeconds = dateFunctions.approxSecondsInTimespan(calendar, repeatFrequency);

  // Calculate the date at the start of the display
  const displayStartDate = dateFunctions.dateWithTimestamp(calendar, startTs, Precision.Second);
  let visibleMarkers = [];

  items.forEach(obj => {
    if (!obj.showAnniversary) {
      return;
    }

    let startDate = obj.startDate;
    if (isDateValid(startDate)) {
      if (avgRepeatSeconds < minRepeatSeconds) {
        const quotient = minRepeatSeconds / avgRepeatSeconds;
        let scale = Math.pow(10, Math.floor(Math.log10(quotient)));
        let multiplier = quotient / scale;
        if (multiplier > 5) {
          multiplier = 1;
          scale = scale * 10;
        } else if (multiplier > 2) {
          multiplier = 5;
        } else if (multiplier > 1) {
          multiplier = 2;
        } else {
          multiplier = 1;
        }

        repeatEveryXFrequency = multiplier * scale;
        repeatFrequency = timespanForPrecisionAndUnits(Precision.Year, repeatEveryXFrequency);
      }

      // When do we stop?
      let stopTs = endTs;
      const type = typesById.get(obj.type);

      if (!type.extendAnniversaryBeyondEnd) {
        if (obj.ongoing) {
          if (dateForNow.timestamp < endTs) {
            stopTs = dateForNow.timestamp;
          }
        } else {
          let deathDate = dateFunctions.dateByAddingSpan(calendar, startDate, obj.duration);
          if (deathDate.timestamp < endTs) {
            stopTs = deathDate.timestamp;
          }
        }
      }

      // And we won't show an anniversary until 1 year after start so add it straight away
      startDate = dateFunctions.dateByAddingSpan(calendar, startDate, repeatFrequency);
      let age = repeatEveryXFrequency;

      // Now, let's work out how many repeats we can immediately jump ahead
      let gap = dateSubtractionFunctions.yearsBetweenDates(calendar, startDate, displayStartDate);
      gap = gap - (gap % repeatEveryXFrequency);
      const color = colorMap.getIn([obj.color, "swatchColor"]);

      // Round down to get to the item before the current start date...
      if (gap > 0) {
        const addSpan = timespanForPrecisionAndUnits(Precision.Year, gap);
        startDate = dateFunctions.dateByAddingSpan(calendar, startDate, addSpan);
        age += gap;
      }

      do {
        if (startDate.timestamp <= stopTs && startDate.timestamp >= startTs) {
          visibleMarkers.push(
            createFromJS.AnniversaryDisplayRecord({
              id: obj.id,
              anniversaryIcon: obj.anniversaryIcon,
              anniversaryLabel: type.anniversaryDisplayLabel,
              itemLabel: getLabelWithUntitled(obj.label),
              color: color,
              age: age,
              date: startDate
            })
          );
        }
        startDate = dateFunctions.dateByAddingSpan(calendar, startDate, repeatFrequency);
        age = age + repeatEveryXFrequency;
      } while (startDate.timestamp <= stopTs);
    }
  });

  return List(visibleMarkers);
};

/**
 *
 * @param {string} id
 * @param {Immutable.List|Array|null} allowedTypes
 * @param {Immutable.Map} itemsById
 * @param {Immutable.Map} typesById
 * @param {List|Array|null} selectedItemIds Find the super in this range. Otherwise, find from all items
 */
const getClosestValidAncestor = (id, allowedTypes, itemsById, typesById, selectedItemIds) => {
  const item = itemsById.get(id);
  if (item) {
    if (!allowedTypes) {
      allowedTypes = typesById.get(item.type).superTypes;
    }
    if (allowedTypes.count === 0 || item.super === null) {
      return null;
    }
    const superItem = itemsById.get(item.super);
    if (allowedTypes.includes(superItem.type) && (!selectedItemIds || selectedItemIds.includes(superItem.id))) {
      return superItem.id;
    }
    return getClosestValidAncestor(superItem.id, allowedTypes, itemsById, typesById);
  }
  return null;
};

// gets the closest ancestor of the item passed in by id that doesn't match the items type.
// recursion feels more natural than iteration here.
const getClosestValidAncestorWithState = (state, id, allowedTypes) => {
  return getClosestValidAncestor(id, allowedTypes, getItemsById(state), typeSelectors.getTypesById(state));
};

/**
 * @param {TimelineRecord} state
 */
const getSetOfItemIdsWithOngoingDescendants = createSelector(
  // inputs
  getItemsById,
  superAndChildrenSelectors.getAncestorsMapFromState,
  // output
  (itemsById, ancestorsById) => {
    // First strip it down to ongoing items
    const ongoingItems = itemsById.filter(i => i.ongoing);
    // Now create a Set of all of their ancestors
    const result = Set().withMutations(tmp => {
      ongoingItems.forEach(item => tmp.union(ancestorsById.get(item.id)));
    });
    return result;
  }
);

const getImageByItemId = createMapSelector(
  state => getItemsById(state).filter(i => i.image),
  attachmentSelectors.getAttachmentsById,
  (item, attachments) => attachments.get(item.image)
);

const getAttachmentsByItemId = createMapSelector(
  state => getItemsById(state).filter(i => i.attachments.size),
  attachmentSelectors.getAttachmentsById,
  (item, attachments) => item.attachments.map(a => attachments.get(a))
);

const getLinksByItemId = createMapSelector(
  state => getItemsById(state).filter(i => i.links.size),
  attachmentSelectors.getAttachmentsById,
  (item, attachments) => item.attachments.map(a => attachments.get(a))
);

// Not wrapped in reselect yet since it is only used in a reducer function
// If this is ever called from a selector, wrap it then
const getItemsWithTagId = (state, tagId) =>
  getItemsById(state)
    .filter(item => item.tags.contains(tagId))
    .map(i => i.id)
    .toList();

const getChildrenWithDates = createSelector(
  // Get inputs to function
  getItemsWithParent,
  // Calculate result
  items => items.filter(item => item.preferredStartDate && !isNil(item.preferredStartDate.timestamp))
);

const getUnselectedChildrenWithDates = (childrenWithDates, ids, selectedIds) => {
  // Get items that aren't selected
  const excludeIds = selectedIds ? selectedIds : ids;
  const unselectedItems = childrenWithDates.filter(i => !excludeIds.includes(i.id));

  // Recursively get children of all selected items
  return getUnselectedChildren(unselectedItems, ids);
};

const getUnselectedChildren = (items, ids) => {
  const children = items
    .filter(i => ids.includes(i.super))
    .reduce((arr, item) => {
      arr.push(item);
      arr = arr.concat(getUnselectedChildren(items, [item.id]));
      return arr;
    }, []);

  return children;
};

const getChildrenIdsForId = (itemsById, id) =>
  itemsById
    .filter(item => item.super === id)
    .map(item => item.id)
    .toList();

const getChildrenIdsFromItems = createSelector(
  // input
  itemsById => itemsById,
  // output
  itemsById => itemsById.map(item => item.children)
);

const getChildrenIdsById = state => getChildrenIdsFromItems(getItemsById(state));

// This function works out whether items are allowed to have narrative children or not
// Depends on:
// - strict mode
// - item type being a folder
const isItemAllowedNarrativeChildren = (state, itemId, enforceStrict = false) => {
  // If strict mode is not enabled, anything is allowed
  if (!itemId || (!enforceStrict && !narrativeSettingsSelectors.getNarrativeUsesStrictFolderStructure(state))) {
    return true;
  }
  // Otherwise, yes only if type is narrative folder
  const item = getItemFromId(state, itemId);
  const type = typeSelectors.getTypeForId(state, item.type);
  return type.isNarrativeFolder;
};

const getParentIdsViolatingStrictNarrativeStructure = state => {
  return narrativeSelectors
    .getNarrativeTreeIds(state)
    .filter(
      id => !isItemAllowedNarrativeChildren(state, id, true) && narrativeSelectors.itemHasNarrativeChildren(state, id)
    );
};

const depthFirstFlatten = (thisLevel, idsByParent) => {
  let list = [];
  if (thisLevel) {
    thisLevel.forEach(id => {
      list.push(id);
      list.push(...depthFirstFlatten(idsByParent.get(id), idsByParent));
    });
  }
  return list;
};

/**
 * @param {Map} itemIdsByType (0)
 * @param {Map} itemIndexByType (1)
 * @param {List} orderedTypeIds (2)
 * @param {List} superTypeIds (3)
 * @param {Map} superAndChildrenById (4)
 * @param {string} typeId (5)
 * @param {bool} reverseOrder (5)
 */
const getItemTreeGroupsForTypeAndSuperTypes = createCachedSelector(
  // inputs
  (...params) => params[0],
  (...params) => params[1],
  (...params) => params[2],
  (...params) => params[3],
  (...params) => params[4],
  (...params) => params[5],
  (...params) => params[6],
  // result
  (itemIdsByType, itemIndexByType, orderedTypeIds, superTypeIds, superAndChildrenById, typeId, reverseOrder) => {
    // Get the list of IDs we want to sort
    const itemIds = itemIdsByType.get(typeId).toSet();
    const allAncestorIds = itemIds.withMutations(mutatingSet => {
      superTypeIds.forEach(sId => {
        if (itemIdsByType.has(sId)) {
          mutatingSet.union(itemIdsByType.get(sId));
        }
      });
    });
    // Normal order: groups come first, so move this particular item type to the bottom of the order
    // Reverse order: items come first, so move this particular item type to the top of the order
    const finalOrderTypeIds = reverseOrder
      ? orderedTypeIds.filter(id => id !== typeId).insert(0, typeId)
      : orderedTypeIds.filter(id => id !== typeId).push(typeId);

    const findItemTypeFromIndexList = id => itemIndexByType.findKey(indexMap => indexMap.has(id));

    const sortByTypeThenPosition = (a, b) => {
      const aType = findItemTypeFromIndexList(a);
      const bType = findItemTypeFromIndexList(b);
      // if the types don't match, sort by type
      if (aType !== bType) {
        return finalOrderTypeIds.indexOf(aType) - finalOrderTypeIds.indexOf(bType);
      }
      // otherwise, sort by list position
      const indexMap = itemIndexByType.get(aType);
      return indexMap.get(a) - indexMap.get(b);
    };

    // Group the IDs
    const itemGroupsByParentId = List(allAncestorIds)
      .groupBy(id => superAndChildrenById.get(id).super || "")
      .map(ids => ids.sort(sortByTypeThenPosition));

    return itemGroupsByParentId;
  }
)((...params) => params[5]);

/** This function returns a Map (by id) containing lists of sorted items that belong underneath
 *  that parent.
 *  The root items are grouped under a parent id of an empty string (since Maps cannot hold null keys)
 * @param {TimelineRecord} state
 * @param {TimelineRecord} typeId
 */
const getItemTreeGroupsForTypeAndSuperTypesFromState = createSelector(
  // inputs
  getItemIdsByType,
  getItemIndexByType,
  typeSelectors.getOrderedTypeIds,
  typeSelectors.getTypesById,
  superAndChildrenSelectors.getSuperAndChildrenById,
  (state, typeId) => typeId,
  // output
  (itemIdsByType, itemIndexByType, orderedTypeIds, typesById, superAndChildrenById, typeId) => {
    const superTypes = typeSelectors.getAllSuperTypesFromTypeMap(typesById, typeId);
    return getItemTreeGroupsForTypeAndSuperTypes(
      itemIdsByType,
      itemIndexByType,
      orderedTypeIds,
      superTypes,
      superAndChildrenById,
      typeId,
      false
    );
  }
);

/**
 * @param {Map} itemIdsByType (0)
 * @param {Map} itemIndexByType (1)
 * @param {List} orderedTypeIds (2)
 * @param {List} superTypeIds (3)
 * @param {Map} superAndChildrenById (4)
 * @param {string} typeId (5)
 */
const getItemIdsInTreeOrderForType = createCachedSelector(
  // inputs
  (...params) => params[0],
  (...params) => params[1],
  (...params) => params[2],
  (...params) => params[3],
  (...params) => params[4],
  (...params) => params[5],
  // result
  (itemIdsByType, itemIndexByType, orderedTypeIds, superTypeIds, superAndChildrenById, typeId) => {
    const itemGroupsByParentId = getItemTreeGroupsForTypeAndSuperTypes(
      itemIdsByType,
      itemIndexByType,
      orderedTypeIds,
      superTypeIds,
      superAndChildrenById,
      typeId,
      false
    );
    // Get the list of IDs that are actually of this type
    const indexesForType = itemIndexByType.get(typeId);
    // Flatten the list, and then prune to include only those of the correct type
    const flattenedList = List(depthFirstFlatten(itemGroupsByParentId.get(""), itemGroupsByParentId)).filter(id =>
      indexesForType.has(id)
    );
    return flattenedList;
  }
)((...params) => params[5]);

/**
 * @param {TimelineRecord} state (0)
 * @param {string} typeId (1)
 */
const getItemIdsInTreeOrderForTypeFromState = (state, typeId) => {
  const itemIdsByType = getItemIdsByType(state);
  const itemIndexByType = getItemIndexByType(state);
  const orderedTypeIds = typeSelectors.getOrderedTypeIds(state);
  const typesById = typeSelectors.getTypesById(state);
  const superAndChildrenById = superAndChildrenSelectors.getSuperAndChildrenById(state);
  const superTypes = typeSelectors.getAllSuperTypesFromTypeMap(typesById, typeId);
  return getItemIdsInTreeOrderForType(
    itemIdsByType,
    itemIndexByType,
    orderedTypeIds,
    superTypes,
    superAndChildrenById,
    typeId
  );
};

/**
 * @param {TimelineRecord} state (0)
 */
const getItemIdsInTreeOrderByTypeFromState = createSelector(
  // inputs
  getItemIdsByType,
  getItemIndexByType,
  typeSelectors.getOrderedTypeIds,
  typeSelectors.getTypesById,
  superAndChildrenSelectors.getSuperAndChildrenById,
  // output
  (itemIdsByType, itemIndexByType, orderedTypeIds, typesById, superAndChildrenById) => {
    return typesById
      .filter(t => !t.showInSequence)
      .map((type, typeId) => {
        const superTypes = typeSelectors.getAllSuperTypesFromTypeMap(typesById, typeId);
        return getItemIdsInTreeOrderForType(
          itemIdsByType,
          itemIndexByType,
          orderedTypeIds,
          superTypes,
          superAndChildrenById,
          typeId
        );
      });
  }
);

/** Creates a master order list for items, comprising:
 *   - sequence
 *   - for non-sequence type, a tree-based order
 * @param {TimelineRecord} state
 */
const getItemIndexesInTreeOrderForAllTypes = createSelector(
  // inputs
  getItemIdsByType,
  getItemIndexByType,
  typeSelectors.getOrderedTypeIds,
  typeSelectors.getTypesById,
  superAndChildrenSelectors.getSuperAndChildrenById,
  sequenceSelectors.getItemSequence,
  // outputs
  (itemIdsByType, itemIndexByType, orderedTypeIds, typesById, superAndChildrenById, sequence) => {
    // const sw = new Stopwatch();
    // sw.start("getValidatedDependenciesForToday top");

    const sortedNonSequenceItems = orderedTypeIds
      .filter(tId => !typesById.get(tId).showInSequence)
      .reduce((result, typeId) => {
        const superTypes = typeSelectors.getAllSuperTypesFromTypeMap(typesById, typeId);
        const itemIdsOfType = getItemIdsInTreeOrderForType(
          itemIdsByType,
          itemIndexByType,
          orderedTypeIds,
          superTypes,
          superAndChildrenById,
          typeId
        );

        return result.concat(itemIdsOfType);
      }, List());

    // sw.lap("getValidatedDependenciesForToday - sortedNonSequenceItems");

    const result = listToIndexMap(sequence.concat(sortedNonSequenceItems));

    // sw.lap("getValidatedDependenciesForToday - listToIndexMap");

    return result;
  }
);

export default {
  getTopLevelItems,
  getEventStartTs,
  getEarliestEventFromIds,
  getEarliestItemOfAll,
  getLatestItemOfAll,
  getEarliestTimelineEvent,
  getLatestTimelineEvent,
  getAllEventIds,
  getAllEventIdsDisplayedOnTimeline,
  getItemIsEvent,
  getItemIsLocked,
  getItemsAreEvents,
  getImageIdsForItems,
  getAttachmentIdsForItems,
  getItemsAreUnlocked,
  getItemIsAllowedInNarrative,
  getItemsAreAllowedInNarrative,
  getAllItemIdsAllowedInNarrative,
  getItemIsAllowedInSequence,
  getItemsAreAllowedInSequence,
  getItemIsAllowedToDisplayInTimeline,
  getItemsAreAllowedToDisplayInTimeline,
  getItemsWithTypeAllowedToDisplayInTimeline,
  getItemsWithAnniversaries,
  getItemsWithAnniversariesMap,
  getDisplayAnniversariesBetweenDates,
  getClosestValidAncestor,
  getClosestValidAncestorWithState,
  getItemsGroupedBySuper,
  getNarrativePositionsForItems,
  getItemsAreInNarrative,
  getItemsWithSetDates,

  // Originally was entity selectors
  getItemsWithIdsFromItems,
  getItemIdsOfType,
  getItemIdsOfTypes,
  getItemIdsOfTypesWithParentTypes,
  getItemTreeGroupsForTypeAndSuperTypesFromState,
  getItemTreeGroupsForTypeAndSuperTypes,
  getItemIdsInTreeOrderForTypeFromState,
  getItemIdsInTreeOrderByTypeFromState,
  getItemIndexesInTreeOrderForAllTypes,
  getItemsOfType,
  getEntityLabelForGuid,
  getEntityValuesForGuid,
  getItemIdsThatReferenceEntity,
  getItemIdsThatReferenceEntity_RESELECT_SAFE,
  getEarliestEventThatReferencesEntity,
  getReferenceIdsThatReferenceEntity,
  getItemsWithParent,

  getItemSwatchColor,

  getTimestampRange,
  getDateRangeForTimelineContent,

  // Used for filter
  getItemIdsByType,
  getItemIdSetByType,
  getItemIndexByTypeFromMap,
  getItemIndexByType,
  getItemsOfTypes,
  getItemsById,
  getItemIds,
  getItemFromId,
  getItemsAsList,
  getItemTypesForIds,

  // Was in type selectors
  getSidebarTypesHasItem,
  getAllowedTypeOptionsByItems,

  getChildrenIdsById,
  getChildrenIdsFromItems,

  // Used for relationship view
  getColumnItemIdsByType,
  getColumnItemIds,

  getInitialsForItem,
  getShortLabelForItem,
  getCompactDisplayForItem,

  //used for attachments
  getAttachmentsByItemId,
  getLinksByItemId,
  getImageByItemId,

  getItemsWithTagId,
  // Used for confirmation when moving parent items
  getChildrenWithDates,
  getUnselectedChildrenWithDates,

  getChildrenIdsForId,

  isItemAllowedNarrativeChildren,
  getParentIdsViolatingStrictNarrativeStructure,

  getSetOfItemIdsWithOngoingDescendants
};
